import { apiUrl } from "../components/ApiManager/AxiosHeaders";
import { fetchCommissionInvoice } from "../components/reports/commissions/commissionsApi";
import { mapCommissionInvoiceToWizardRecord } from "../components/reports/commissions/commissionInvoiceRecordMapper";
import { buildMultipartFormDataRequestConfig } from "./financeInvoicePaymentHelpers";
import {
  getFinanceInvoiceEmailApiMessage,
  isFinanceInvoiceEmailApiSuccess,
  loadInvoicePreviewDrafts,
} from "./financeInvoiceEmailHelpers";
import {
  buildInvoiceXeroPreviewDraftFromRecord,
} from "./invoiceEditHelpers";
import {
  mapRowToInvoiceForm,
  normalizeInvoiceSettingRecord,
} from "./invoiceSettingHelpers";

export function buildInvoicePdfFilename(invoiceNumber = "") {
  const safe = String(invoiceNumber || "invoice")
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return safe.toLowerCase().endsWith(".pdf") ? safe : `${safe || "invoice"}.pdf`;
}

export async function previewFinanceInvoiceEmailRecipient(invoiceId) {
  const response = await apiUrl.get(
    `finance-invoices/${invoiceId}/email-recipient`,
  );
  if (!isFinanceInvoiceEmailApiSuccess(response)) {
    throw new Error(
      getFinanceInvoiceEmailApiMessage(
        response,
        "Failed to load email recipient.",
      ),
    );
  }
  return response?.data?.data || response?.data || {};
}

async function resolveInvoiceSetting() {
  const settingResponse = await apiUrl.get("/getinvoiceSetting");
  return (
    normalizeInvoiceSettingRecord(settingResponse) ||
    (settingResponse?.status
      ? mapRowToInvoiceForm(settingResponse?.data?.data || {})
      : null)
  );
}

export async function loadFinanceInvoiceApproveEmailDraft(
  invoiceId,
  { isCommission = false, clientId = null } = {},
) {
  const invoiceSetting = await resolveInvoiceSetting();

  if (isCommission) {
    const full = await fetchCommissionInvoice(invoiceId);
    const invoice = mapCommissionInvoiceToWizardRecord(full);
    const draft = buildInvoiceXeroPreviewDraftFromRecord(invoice, {
      invoiceSetting,
    });
    return {
      draft,
      invoice_number: draft?.invoiceNumber || invoice?.invoice_number || "",
    };
  }

  const drafts = await loadInvoicePreviewDrafts([invoiceId], { clientId });
  const first = drafts?.[0] || null;
  return {
    draft: first?.draft || null,
    invoice_number: first?.invoice_number || first?.draft?.invoiceNumber || "",
  };
}

async function postFinanceInvoiceEmailFormData(formData) {
  const response = await apiUrl.post(
    "sendfinanceinvoiceemail",
    formData,
    buildMultipartFormDataRequestConfig(),
  );

  if (!isFinanceInvoiceEmailApiSuccess(response)) {
    throw new Error(
      getFinanceInvoiceEmailApiMessage(
        response,
        "Failed to send invoice email.",
      ),
    );
  }

  return response?.data?.data || response?.data || {};
}

function appendSharedFinanceInvoiceEmailFields(formData, {
  clientId,
  recipientName,
  email,
  ccEmails,
  subject,
  emailBody,
}) {
  if (clientId) formData.append("client_id", String(clientId));
  if (recipientName) formData.append("name", recipientName);
  formData.append("email", String(email || "").trim());
  formData.append("subject", String(subject || "").trim());
  formData.append("email_body", String(emailBody || "").trim());
  (Array.isArray(ccEmails) ? ccEmails : []).forEach((ccEmail, index) => {
    formData.append(`cc_emails[${index}]`, String(ccEmail || "").trim());
  });
}

export async function sendFinanceInvoiceApproveEmail({
  invoiceId,
  clientId = null,
  recipientName = "",
  email,
  ccEmails = [],
  subject,
  emailBody,
  pdfBlob,
  pdfFilename = "invoice.pdf",
  attachments = null,
  approve = false,
}) {
  const attachmentList = Array.isArray(attachments)
    ? attachments.filter((item) => item?.pdfBlob && item?.invoiceId)
    : pdfBlob && invoiceId
      ? [
          {
            invoiceId,
            pdfBlob,
            pdfFilename: pdfFilename || buildInvoicePdfFilename(),
          },
        ]
      : [];

  if (!attachmentList.length) {
    throw new Error("Invoice PDF attachment is missing.");
  }

  const sharedFields = {
    clientId,
    recipientName,
    email,
    ccEmails,
    subject,
    emailBody,
  };

  const formData = new FormData();
  const primaryAttachment = attachmentList[0];
  const primaryFilename =
    primaryAttachment.pdfFilename || buildInvoicePdfFilename();

  formData.append("invoice_id", String(primaryAttachment.invoiceId));
  formData.append("pdf", primaryAttachment.pdfBlob, primaryFilename);

  attachmentList.forEach((attachment, index) => {
    formData.append(`invoice_ids[${index}]`, String(attachment.invoiceId));
    formData.append(
      `pdfs[${index}]`,
      attachment.pdfBlob,
      attachment.pdfFilename || buildInvoicePdfFilename(),
    );
  });

  formData.append("approve", approve ? "1" : "0");

  appendSharedFinanceInvoiceEmailFields(formData, sharedFields);
  return postFinanceInvoiceEmailFormData(formData);
}
